// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xLoadingOverlay } from "@ces/App/Handlers/XNG/Common";
import { CustomAppState } from "@ces/App/Store/Reducers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import React, { FC, useEffect, useState } from "react";
import * as ReactDOM from "react-dom";
import { shallowEqual, useDispatch } from "react-redux";
import { css } from "styled-components";

/**
 * Steps:
 * Add "hideLoadingOverlay" code inside PreStyle/globalStyle
 */
export const hideLoadingOverlay = css`
    #react-app.remove-ootb-spinners {
        div[data-test-selector="loadingOverlaySpinner"],
        svg[class^="LoadingSpinnerStyle"] {
            visibility: hidden;
            width: 0;
            height: 0;
            position: absolute;
            top: -10000px;
        }
    }
`;

export const zIndexLoadingOverlay = 1500;

/**
 * Add this style in XDL/GlobalStyle
 */
export const xngLoadingOverlayUtilitiesStyles = css`
    #xng-loading-overlay {
        width: 100%;
        position: fixed;
        height: 100%;
        z-index: ${zIndexLoadingOverlay};
        top: 0;
        bottom: 0;
        @media print {
            display: none;
        }
    }
`;

/**
 * Global component uses in Header
 * @constructor
 */
const XngLoadingOverlayUtilities: FC = () => {
    const { isErrorModalOpen, pages, isLoadingOverlay, isSigningIn } = useAppSelector(
        state => ({
            isErrorModalOpen: state.context.isErrorModalOpen,
            pages: state.data.pages,
            isLoadingOverlay: (state as CustomAppState).xCommon.loadingOverlay,
            isSigningIn: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
        }),
        shallowEqual,
    );

    const isShowing = () => isSigningIn || (isLoadingOverlay && isLoadingOverlay?.length > 0);
    const [showLoading, setShowLoading] = useState(isShowing());

    const dispatch = useDispatch();

    useEffect(() => {
        const showXngLoading = isShowing();
        setShowLoading(showXngLoading);

        if (typeof document === "undefined") {
            return;
        }

        /** To hide all loadingOverlay and spinner
         * We added "hideLoadingOverlay" inside PreStyle/globalStyle
         */
        const element = document.getElementById("react-app");
        if (showXngLoading) {
            element?.classList.add(`remove-ootb-spinners`);
        } else {
            element?.classList.remove(`remove-ootb-spinners`);
        }
    }, [isSigningIn, isLoadingOverlay]);

    /** To close LoadingOverlay when the page redirect to another path */
    useEffect(() => {
        dispatch(xLoadingOverlay({ key: "clear", value: "" }));
    }, [pages.location]);

    /** To close LoadingOverlay when ErrorModal open */
    useEffect(() => {
        if (isErrorModalOpen) {
            dispatch(xLoadingOverlay({ key: "clear", value: "" }));
        }
    }, [isErrorModalOpen]);

    const LoadingElement = ({ showLoading }: { showLoading?: boolean }) => (
        <>
            {showLoading && (
                <LoadingOverlay
                    id="xng-loading-overlay"
                    data-test-selector="main-loading-overlay"
                    loading={true}
                    mergeCss={true}
                    css={css`
                        @media print {
                            display: none;
                        }
                    `}
                />
            )}
        </>
    );

    if (IS_SERVER_SIDE) {
        return <LoadingElement showLoading={true} />;
    }

    return ReactDOM.createPortal(<LoadingElement showLoading={showLoading} />, document.body);
};

export default XngLoadingOverlayUtilities;
